import { useState, useMemo } from "react";
import {
  useBookings,
  useUpdateBookingStatus,
  useUpdateBooking,
  useCancelBooking,
  useCreateBooking,
} from "@/hooks/useBookings";
import { useServices } from "@/hooks/useServices";
import {
  useUnavailability,
  useCreateUnavailability,
  useUpdateUnavailability,
  useDeleteUnavailability,
} from "@/hooks/useUnavailability";
import { useDoctors } from "@/hooks/useDoctors";
import { useRoomCalendarData } from "@/hooks/useRoomCalendar";
import { useAuth } from "@/hooks/useAuth";
import { getDoctorName } from "@/utils/dataLookups";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { showSuccess, showError, showInfo } from "@/utils/toastHelpers";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar as CalendarIcon,
  Lock,
  Filter,
  User,
  Stethoscope,
  CheckCircle,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  isSameDay,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isToday,
  addWeeks,
  subWeeks,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import DoctorAppointmentForm from "../components/services/DoctorAppointmentForm";
import TimeGridCalendar from "../components/calendar/TimeGridCalendar";
import AppointmentDetailModal from "../components/services/AppointmentDetailModal";
import ServiceSelectionModal from "../components/services/ServiceSelectionModal";
import UnavailabilityForm from "../components/services/UnavailabilityForm";
import DoctorUnavailabilityView from "../components/services/DoctorUnavailabilityView";
import SlotBookingModal from "../components/booking/SlotBookingModal";

export default function CalendarPage() {
  // Auth hook to get current user
  const { user, isDoctor, isPracticeOwner } = useAuth();

  // React Query hooks for data fetching
  const { data: services = [] } = useServices();
  const { data: appointments = [] } = useBookings();
  const { data: unavailability = [] } = useUnavailability();
  const { data: rooms = [] } = useQuery({
    queryKey: ["rooms"],
    queryFn: () => apiClient.get(ENDPOINTS.SETTINGS.ROOMS),
  });
  // Pre-fetch patients to ensure they're cached when enriching bookings
  const { data: patients = [] } = useQuery({
    queryKey: ["patients"],
    queryFn: () => apiClient.get("/api/patients"),
    staleTime: 10 * 60 * 1000, // 10 minutes - same as enrichBookingsWithRelatedData
  });

  // Mutations
  const updateBookingStatusMutation = useUpdateBookingStatus();
  const updateBookingMutation = useUpdateBooking();
  const cancelBookingMutation = useCancelBooking();
  const createBookingMutation = useCreateBooking();
  const createUnavailabilityMutation = useCreateUnavailability();
  const updateUnavailabilityMutation = useUpdateUnavailability();
  const deleteUnavailabilityMutation = useDeleteUnavailability();

  // UI State
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [viewingAppointment, setViewingAppointment] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);

  const [showServiceSelection, setShowServiceSelection] = useState(false);
  const [serviceForBooking, setServiceForBooking] = useState(null);

  const [showBlockTimeForm, setShowBlockTimeForm] = useState(false);
  const [editingUnavailability, setEditingUnavailability] = useState(null);

  const [showUnavailabilityView, setShowUnavailabilityView] = useState(false);

  const [showSlotBooking, setShowSlotBooking] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedPatient, setSelectedPatient] = useState("all");
  const [selectedDoctor, setSelectedDoctor] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("active"); // "all", "active", "cancelled"

  // Room calendar data for room view (enriched with availability + services)
  const dayOfWeek = selectedDate.getDay(); // 0=Sun, 6=Sat
  const { data: roomCalendarData = [] } = useRoomCalendarData(dayOfWeek);

  const handleScheduleNewAppointment = () => {
    setEditingAppointment(null);
    setServiceForBooking(null);
    setShowServiceSelection(true);
  };

  const handleServiceSelectedForBooking = (service) => {
    setServiceForBooking(service);
    setShowServiceSelection(false);
    setShowAppointmentForm(true);
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setViewingAppointment(null);
    setShowAppointmentForm(true);
  };

  const handleSaveAppointment = async (data) => {
    try {
      if (data.id) {
        // For updates, use the full booking update mutation
        await updateBookingMutation.mutateAsync(data);
      } else {
        // This is a new booking
        await createBookingMutation.mutateAsync(data);
      }
      setShowAppointmentForm(false);
      setEditingAppointment(null);
      setServiceForBooking(null);
      showSuccess("Appointment saved successfully");
    } catch (error) {
      console.error("Error saving appointment:", error);
      showError("Failed to save appointment", error.message);
    }
  };

  const handleEditUnavailability = (unavailabilityItem) => {
    setEditingUnavailability(unavailabilityItem);
    setShowBlockTimeForm(true);
  };

  const handleSaveUnavailability = async (data) => {
    try {
      if (data.id) {
        // This is an update
        await updateUnavailabilityMutation.mutateAsync({
          recordId: data.id,
          unavailabilityData: data,
        });
      } else {
        // This is a new creation
        await createUnavailabilityMutation.mutateAsync(data);
      }
      setShowBlockTimeForm(false);
      setEditingUnavailability(null);
      showSuccess("Blocked time saved successfully");
    } catch (error) {
      console.error("Error saving unavailability:", error);
      showError("Failed to save unavailability", error.message);
    }
  };

  const handleConfirmAppointment = async (appointmentId) => {
    try {
      await updateBookingStatusMutation.mutateAsync({
        bookingId: appointmentId,
        status: "confirmed",
      });
      showSuccess("Appointment confirmed");
    } catch (error) {
      console.error("Error confirming appointment:", error);
      showError("Failed to confirm appointment", error.message);
    }
  };

  const handleCompleteService = async (appointmentId) => {
    try {
      await updateBookingStatusMutation.mutateAsync({
        bookingId: appointmentId,
        status: "completed",
      });
      showSuccess("Service marked as completed");
    } catch (error) {
      console.error("Error completing service:", error);
      showError("Failed to complete service", error.message);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (window.confirm("Are you sure you want to cancel this appointment?")) {
      try {
        await cancelBookingMutation.mutateAsync(appointmentId);

        if (viewingAppointment && viewingAppointment.id === appointmentId) {
          setViewingAppointment(null);
        }
        showSuccess("Appointment cancelled successfully");
      } catch (error) {
        console.error("Failed to cancel appointment:", error);
        showError("Failed to cancel appointment", error.message);
      }
    }
  };

  const handleDeleteUnavailability = async (unavailabilityId) => {
    if (window.confirm("Are you sure you want to remove this blocked time?")) {
      try {
        await deleteUnavailabilityMutation.mutateAsync(unavailabilityId);
        showSuccess("Blocked time removed successfully");
      } catch (error) {
        console.error("Failed to delete unavailability block:", error);
        showError("Failed to delete unavailability", error.message);
      }
    }
  };

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
    setShowSlotBooking(true);
  };

  const handleSlotBookingCreate = async (bookingData) => {
    // Booking is created by SlotBookingModal's internal mutation
    // This callback is for any additional handling needed after successful booking
    setShowSlotBooking(false);
    setSelectedSlot(null);
  };

  // Fetch all doctors for the practice - must be before any useMemo that uses it
  const { data: allDoctors = [] } = useDoctors();

  // Get unique patient IDs from appointments
  const availablePatients = useMemo(() => {
    const patientIds = [
      ...new Set(appointments.map((apt) => apt.patient_id).filter(Boolean)),
    ];
    return patientIds.sort();
  }, [appointments]);

  // Filter appointments for the calendar view (all dates) - MUST BE BEFORE appointmentsForSelectedDay
  const filteredAppointments = useMemo(() => {
    return appointments.filter((apt) => {
      const patientMatch =
        selectedPatient === "all" || apt.patient_id === selectedPatient;

      // Handle doctor filtering inline to avoid dependency issues
      let doctorMatch = true;
      if (selectedDoctor !== "all") {
        const doctorName = getDoctorName(selectedDoctor, allDoctors);
        doctorMatch = apt.provider_name === doctorName;
      }

      // Status filtering
      let statusMatch = false;
      if (selectedStatus === "all") {
        statusMatch = true;
      } else if (selectedStatus === "active") {
        statusMatch = apt.status !== "cancelled";
      } else if (selectedStatus === "cancelled") {
        statusMatch = apt.status === "cancelled";
      }

      return patientMatch && doctorMatch && statusMatch;
    });
  }, [
    appointments,
    selectedPatient,
    selectedDoctor,
    selectedStatus,
    allDoctors,
  ]);

  // Filter unavailability for the calendar view (all dates) - MUST BE BEFORE unavailabilityForSelectedDay
  const filteredUnavailability = useMemo(() => {
    if (selectedDoctor === "all") {
      return unavailability;
    }

    // Handle doctor filtering inline to avoid dependency issues
    const doctorName = getDoctorName(selectedDoctor, allDoctors);

    return unavailability.filter((u) => u.provider_name === doctorName);
  }, [unavailability, selectedDoctor, allDoctors]);

  const appointmentsForSelectedDay = useMemo(() => {
    if (!Array.isArray(filteredAppointments) || !selectedDate) {
      return [];
    }

    return filteredAppointments
      .filter((apt) => {
        try {
          if (!apt || !apt.appointment_date) return false;
          const appointmentDate = new Date(apt.appointment_date);
          return isSameDay(appointmentDate, selectedDate);
        } catch (error) {
          console.error("Error comparing dates:", error);
          return false;
        }
      })
      .sort((a, b) => {
        try {
          return new Date(a.appointment_date) - new Date(b.appointment_date);
        } catch (error) {
          console.error("Error sorting appointments:", error);
          return 0;
        }
      });
  }, [filteredAppointments, selectedDate]);

  const unavailabilityForSelectedDay = useMemo(() => {
    if (!Array.isArray(filteredUnavailability) || !selectedDate) {
      return [];
    }
    return filteredUnavailability
      .filter((u) => {
        try {
          if (!u || !u.start_time) return false;
          const dateMatch = isSameDay(new Date(u.start_time), selectedDate);
          return dateMatch;
        } catch (e) {
          console.error("Error filtering unavailability:", e);
          return false;
        }
      })
      .sort((a, b) => {
        try {
          return new Date(a.start_time) - new Date(b.start_time);
        } catch (error) {
          console.error("Error sorting unavailability:", error);
          return 0;
        }
      });
  }, [filteredUnavailability, selectedDate]);

  // For Calendar filtering, show all doctors and practice owners in the practice (not just those with appointments)
  const availableDoctors = useMemo(() => {
    return allDoctors
      .filter((doctor) => {
        // Include active doctors and practice owners
        return (
          doctor.status === "active" &&
          (doctor.userType === "doctor" || doctor.userType === "practiceOwner")
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allDoctors]);

  const clearAllFilters = () => {
    setSelectedPatient("all");
    setSelectedDoctor("all");
    setSelectedStatus("active");
  };

  const hasActiveFilters =
    selectedPatient !== "all" ||
    selectedDoctor !== "all" ||
    selectedStatus !== "active";

  return (
    <div className="min-h-screen flex flex-col">
      {/* Fixed Header and Filters */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
        <div className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                Appointment Calendar
              </h1>
              <p className="text-slate-600">
                Manage clinic schedules and patient appointments.
              </p>
            </div>
            <div className="flex gap-2">
              {(isDoctor || isPracticeOwner) && (
                <Button
                  onClick={() =>
                    setShowUnavailabilityView(!showUnavailabilityView)
                  }
                  variant="outline"
                  className="border-slate-300 hover:bg-slate-50"
                  data-testid="unavailability-toggle"
                >
                  {showUnavailabilityView ? (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      View Calendar
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-4 h-4 mr-2" />
                      My Unavailability
                    </>
                  )}
                </Button>
              )}
              <Button
                onClick={() => {
                  setEditingUnavailability(null);
                  setShowBlockTimeForm(true);
                }}
                variant="outline"
                className="border-slate-300 hover:bg-slate-50"
                data-testid="block-time-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Block Time
              </Button>
              <Button
                onClick={handleScheduleNewAppointment}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
                data-testid="schedule-appointment-btn"
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                Schedule Appointment
              </Button>
            </div>
          </div>
        </div>

        {/* Filters - Only show when not viewing unavailability */}
        {!showUnavailabilityView && (
          <div className="px-6 pb-4">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">
                  Filters:
                </span>
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" />
                  <Select
                    value={selectedPatient}
                    onValueChange={setSelectedPatient}
                  >
                    <SelectTrigger className="w-48 bg-white/60 backdrop-blur-sm border-slate-200/60" data-testid="calendar-patient-filter">
                      <SelectValue placeholder="All Patients" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="calendar-patient-option-all">All Patients</SelectItem>
                      {availablePatients.map((patientId, index) => (
                        <SelectItem key={patientId} value={patientId} data-testid={`calendar-patient-option-${index}`}>
                          {patientId}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-slate-500" />
                  <Select
                    value={selectedDoctor}
                    onValueChange={setSelectedDoctor}
                  >
                    <SelectTrigger className="w-48 bg-white/60 backdrop-blur-sm border-slate-200/60" data-testid="calendar-doctor-filter">
                      <SelectValue placeholder="All Doctors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Doctors</SelectItem>
                      {availableDoctors.map((doctor) => (
                        <SelectItem key={doctor.id} value={doctor.id}>
                          {getDoctorName(doctor.id, allDoctors)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-slate-500" />
                  <Select
                    value={selectedStatus}
                    onValueChange={setSelectedStatus}
                  >
                    <SelectTrigger className="w-48 bg-white/60 backdrop-blur-sm border-slate-200/60" data-testid="calendar-status-filter">
                      <SelectValue placeholder="Appointment Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">
                        Active Appointments
                      </SelectItem>
                      <SelectItem value="cancelled">
                        Cancelled Appointments
                      </SelectItem>
                      <SelectItem value="all">All Appointments</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-slate-600 hover:text-slate-800"
                    data-testid="calendar-clear-filters"
                  >
                    Clear All Filters
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content - scrollable */}
      <div className="flex-1 p-6 overflow-auto">
        {showUnavailabilityView ? (
          <DoctorUnavailabilityView
            onEdit={(block) => {
              setEditingUnavailability(block);
              setShowBlockTimeForm(true);
            }}
            onAddNew={() => {
              setEditingUnavailability(null);
              setShowBlockTimeForm(true);
            }}
            showOnlyFuture={true}
          />
        ) : (
          <>
            {/* Full-width Time Grid Calendar */}
            <div className="w-full">
              <TimeGridCalendar
                  selectedDate={selectedDate}
                  appointments={filteredAppointments}
                  unavailability={filteredUnavailability}
                  appointmentsForSelectedDay={appointmentsForSelectedDay}
                  unavailabilityForSelectedDay={unavailabilityForSelectedDay}
                  services={services}
                  doctors={allDoctors}
                  rooms={rooms}
                  roomCalendarData={roomCalendarData}
                  selectedDoctor={selectedDoctor}
                  onViewAppointment={setViewingAppointment}
                  onComplete={handleCompleteService}
                  onDeleteAppointment={handleDeleteAppointment}
                  onDeleteUnavailability={handleDeleteUnavailability}
                  onEditUnavailability={handleEditUnavailability}
                  onDateSelect={setSelectedDate}
                  onSlotClick={handleSlotClick}
                  startHour={0}
                  endHour={24}
                  timeSlotMinutes={15}
                />
            </div>
          </>
        )}

        {/* Forms and Modals */}
        {showServiceSelection && (
          <ServiceSelectionModal
            services={services}
            onSelect={handleServiceSelectedForBooking}
            onClose={() => setShowServiceSelection(false)}
          />
        )}

        {showBlockTimeForm && (
          <UnavailabilityForm
            unavailabilityToEdit={editingUnavailability}
            onSave={handleSaveUnavailability}
            onClose={() => {
              setShowBlockTimeForm(false);
              setEditingUnavailability(null);
            }}
            doctors={allDoctors}
          />
        )}

        {showAppointmentForm && (
          <DoctorAppointmentForm
            appointmentToEdit={editingAppointment}
            serviceForBooking={serviceForBooking}
            services={services}
            onSave={handleSaveAppointment}
            onClose={() => {
              setShowAppointmentForm(false);
              setEditingAppointment(null);
              setServiceForBooking(null);
            }}
          />
        )}

        {viewingAppointment && (
          <AppointmentDetailModal
            appointment={viewingAppointment}
            services={services}
            rooms={rooms}
            onClose={() => setViewingAppointment(null)}
            onConfirm={handleConfirmAppointment}
            onComplete={handleCompleteService}
            onDelete={handleDeleteAppointment}
            onEdit={handleEditAppointment}
          />
        )}

        {showSlotBooking && (
          <SlotBookingModal
            isOpen={showSlotBooking}
            onClose={() => {
              setShowSlotBooking(false);
              setSelectedSlot(null);
            }}
            selectedSlot={selectedSlot}
            onBookingCreate={handleSlotBookingCreate}
          />
        )}
      </div>
    </div>
  );
}
